import {
	useGetAvailableExternalFieldsQuery,
	useGetAvailableLevitateFieldsQuery,
	useGetExternalIntegrationFieldMappingsQuery,
} from '@Queries/index';
import { baseStyleSheet, bs } from '@Styles/styles';
import * as Api from '@ViewModels';
import { Badge } from '@WebComponents/dataBoards/Badge';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '@WebComponents/DataGrid';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@WebComponents/Select';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../../web/components/Button';
import { Modal } from '../../../../../../../web/components/Modal';
import { TrashIcon } from '../../../../../../../web/components/svgs/icons/TrashIcon';
import { SettingGroup } from '../SettingsView/components/SettingGroup';
import { EditableButton, EditableSelect, useEditMode } from '../SettingsView/context';
import { styleSheet } from './styles';

interface IFieldMappingsViewProps {
	impersonationContext: Api.IImpersonationContext;
	fieldMappingsQuery: ReturnType<typeof useGetExternalIntegrationFieldMappingsQuery>;
	fieldMappings: Api.IIntegrationFieldMapping[];
	setFieldMappings: (fieldMappings: Api.IIntegrationFieldMapping[]) => void;
	externalIntegration: Api.IExternalIntegration;
	onDeletedFieldMappingsChanged?: (deletedMappings: Api.IIntegrationFieldMapping[]) => void;
}

export const FieldMappingsView: React.FC<IFieldMappingsViewProps> = ({
	impersonationContext,
	fieldMappingsQuery,
	fieldMappings,
	setFieldMappings,
	externalIntegration,
	onDeletedFieldMappingsChanged,
}) => {
	const provider = externalIntegration.provider;
	const contactRecordType = (externalIntegration.settings as Api.ISalesforceSettings).contactRecordType;
	const { isEditing } = useEditMode();
	const [filteredObjectType, setFilteredObjectType] = React.useState<string | null>(null);

	const availableLevitateFieldsQuery = useGetAvailableLevitateFieldsQuery({
		impersonationContext,
		integrationProvider: provider,
	});

	const availableExternalFieldsQuery = useGetAvailableExternalFieldsQuery({
		impersonationContext,
		integrationProvider: provider,
		objectType: filteredObjectType,
		enabled: !externalIntegration.configuration.needsConfiguration && contactRecordType !== 'Unknown',
	});
	const availableExternalFieldOptions =
		availableExternalFieldsQuery.data?.map(field => {
			return {
				label: `${field.displayName} (${field.name})`,
				value: field.name,
			};
		}) ?? [];

	// Track original field mappings to identify unsaved changes
	const [originalFieldMappings, setOriginalFieldMappings] = React.useState<Api.IIntegrationFieldMapping[]>([]);

	// Track field mappings marked for deletion
	const [deletedFieldMappings, setDeletedFieldMappings] = React.useState<Set<string>>(new Set());

	// Track newly added field mappings (not yet saved to server)
	const [newFieldMappings, setNewFieldMappings] = React.useState<Set<string>>(new Set());

	React.useEffect(() => {
		const mappings = fieldMappingsQuery.data ?? [];
		setFieldMappings(mappings);
		setOriginalFieldMappings(mappings);
		// Reset deleted and new mappings when data changes
		setDeletedFieldMappings(new Set());
		setNewFieldMappings(new Set());
	}, [setFieldMappings, fieldMappingsQuery.data]);

	// Notify parent of deleted field mappings changes
	React.useEffect(() => {
		if (onDeletedFieldMappingsChanged) {
			const deletedMappings = originalFieldMappings.filter(original => {
				const mappingKey = `${original.externalObjectType}-${original.externalFieldName}`;
				return deletedFieldMappings.has(mappingKey);
			});
			onDeletedFieldMappingsChanged(deletedMappings);
		}
	}, [deletedFieldMappings, originalFieldMappings, onDeletedFieldMappingsChanged]);

	const isLoading = fieldMappingsQuery.isLoading;

	const [showAddMappingModal, setShowAddMappingModal] = React.useState(false);
	const [newMapping, setNewMapping] = React.useState<Partial<Api.IIntegrationFieldMapping>>({
		externalObjectType: contactRecordType,
		externalFieldName: '',
		levitateFieldName: '',
		displayName: '',
	});

	// Get unique object types from field mappings
	const uniqueObjectTypes = React.useMemo(() => {
		const types = new Set<string>();
		fieldMappingsQuery.data?.forEach(item => {
			types.add(item.externalObjectType);
		});
		return Array.from(types).sort();
	}, [fieldMappingsQuery.data]);

	const objectTypeOptions = React.useMemo(
		() => [
			{ value: 'all', label: 'All Types' },
			...uniqueObjectTypes.map(type => ({
				value: type,
				label: type,
			})),
		],
		[uniqueObjectTypes]
	);

	const onObjectTypeFilterChanged = (value: string) => {
		if (value === 'all') {
			setFilteredObjectType(null);
			return;
		}
		setFilteredObjectType(value);
		availableExternalFieldsQuery.refetch();
	};

	const onLevitateFieldNameChanged = (index: number, levitateFieldName: string) => {
		const updatedFieldMappings = fieldMappings.map((mapping, i) => {
			return i !== index
				? mapping
				: {
						...mapping,
						levitateFieldName,
					};
		});
		setFieldMappings(updatedFieldMappings);
	};

	const onDeleteFieldMapping = (mapping: Api.IIntegrationFieldMapping) => {
		const mappingKey = `${mapping.externalObjectType}-${mapping.externalFieldName}`;

		// If this is a newly added mapping (not yet saved), remove it immediately
		if (isFieldMappingNew(mapping)) {
			setFieldMappings(
				fieldMappings.filter(
					m =>
						!(m.externalObjectType === mapping.externalObjectType && m.externalFieldName === mapping.externalFieldName)
				)
			);
			setNewFieldMappings(prev => {
				const newSet = new Set(prev);
				newSet.delete(mappingKey);
				return newSet;
			});
		} else {
			// For existing mappings, mark for deletion
			setDeletedFieldMappings(prev => new Set(prev).add(mappingKey));
		}
	};

	const onUndeleteFieldMapping = (mapping: Api.IIntegrationFieldMapping) => {
		// Remove field mapping from deleted list
		const mappingKey = `${mapping.externalObjectType}-${mapping.externalFieldName}`;
		setDeletedFieldMappings(prev => {
			const newSet = new Set(prev);
			newSet.delete(mappingKey);
			return newSet;
		});
	};

	const onUndoFieldMappingChanges = (mapping: Api.IIntegrationFieldMapping) => {
		// Find the original mapping and restore its values
		const originalMapping = originalFieldMappings.find(
			orig =>
				orig.externalObjectType === mapping.externalObjectType && orig.externalFieldName === mapping.externalFieldName
		);

		if (originalMapping) {
			const updatedFieldMappings = fieldMappings.map(m =>
				m.externalObjectType === mapping.externalObjectType && m.externalFieldName === mapping.externalFieldName
					? { ...m, levitateFieldName: originalMapping.levitateFieldName }
					: m
			);
			setFieldMappings(updatedFieldMappings);
		}
	};

	// Helper to check if a mapping is marked for deletion
	const isFieldMappingDeleted = (mapping: Api.IIntegrationFieldMapping) => {
		const mappingKey = `${mapping.externalObjectType}-${mapping.externalFieldName}`;
		return deletedFieldMappings.has(mappingKey);
	};

	// Helper to check if a mapping is newly added (not yet saved)
	const isFieldMappingNew = (mapping: Api.IIntegrationFieldMapping) => {
		const mappingKey = `${mapping.externalObjectType}-${mapping.externalFieldName}`;
		return newFieldMappings.has(mappingKey);
	};

	const onShowAddMapping = () => {
		setShowAddMappingModal(true);
	};

	const onAddMappingModalClose = (shouldAdd: boolean) => {
		if (shouldAdd && newMapping.externalFieldName && newMapping.levitateFieldName) {
			const mappingToAdd: Api.IIntegrationFieldMapping = {
				externalObjectType: newMapping.externalObjectType || 'Contact',
				externalFieldName: newMapping.externalFieldName,
				levitateFieldName: newMapping.levitateFieldName,
				displayName: newMapping.displayName || newMapping.externalFieldName,
				isRequired: false,
			};
			setFieldMappings([mappingToAdd, ...fieldMappings]);

			// Mark this mapping as newly added
			const mappingKey = `${mappingToAdd.externalObjectType}-${mappingToAdd.externalFieldName}`;
			setNewFieldMappings(prev => new Set(prev).add(mappingKey));
		}

		setShowAddMappingModal(false);
		setNewMapping({
			externalObjectType: contactRecordType,
			externalFieldName: '',
			levitateFieldName: '',
			displayName: '',
			isRequired: false,
		});
	};

	// Helper functions for field lookups
	const getLevitateFieldDisplayName = (propertyName: string) => {
		const field = availableLevitateFieldsQuery.data?.find(f => f.propertyName === propertyName);
		return field ? field.name : propertyName;
	};

	const getLevitateFieldPropertyName = (displayName: string) => {
		const field = availableLevitateFieldsQuery.data?.find(f => f.name === displayName);
		return field ? field.propertyName : displayName;
	};

	const getExternalFieldInfo = (fieldName: string) => {
		return availableExternalFieldsQuery.data?.find(f => f.name === fieldName);
	};

	// Helper function to check if a field mapping is unsaved
	const isFieldMappingUnsaved = React.useCallback(
		(mapping: Api.IIntegrationFieldMapping) => {
			const original = originalFieldMappings.find(
				orig =>
					orig.externalObjectType === mapping.externalObjectType && orig.externalFieldName === mapping.externalFieldName
			);

			if (!original) {
				// This is a new mapping
				return true;
			}

			// Check if levitate field name has changed
			return original.levitateFieldName !== mapping.levitateFieldName;
		},
		[originalFieldMappings]
	);

	const filteredFieldMappings = React.useMemo(() => {
		return fieldMappings.filter(item => {
			if (!filteredObjectType) return true;
			return item.externalObjectType === filteredObjectType;
		});
	}, [fieldMappings, filteredObjectType]);

	return (
		<SettingGroup title='Field Mappings'>
			<div className={css(styleSheet.subtitle)}>
				Manage field mappings for this integration. These mappings determine which external system fields should be
				synchronized with Levitate fields for each object type.
			</div>
			<div className={css(styleSheet.actionBar)}>
				<div className={css(styleSheet.filterSection)}>
					<span className={css(styleSheet.filterLabel)}>Object Type:</span>
					<Select
						items={objectTypeOptions}
						value={filteredObjectType || 'all'}
						onValueChange={onObjectTypeFilterChanged}
					>
						<SelectTrigger>
							<SelectValue placeholder={filteredObjectType ? filteredObjectType : 'All Types'} />
						</SelectTrigger>
						<SelectContent>
							{objectTypeOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className={css(styleSheet.buttonGroup)}>
					<EditableButton displayValue='Add Field Mapping' onClick={onShowAddMapping} />
				</div>
			</div>

			<div className={css(styleSheet.tableContainer)}>
				<DataGrid styles={[styleSheet.tableOverrides]}>
					<DataGridColumns>
						<DataGridColumn sticky styles={[styleSheet.columnOne]}>
							External Field
						</DataGridColumn>
						<DataGridColumn sticky styles={[styleSheet.columnTwo]}>
							Required
						</DataGridColumn>
						<DataGridColumn sticky styles={[styleSheet.columnThree]}>
							Levitate Field
						</DataGridColumn>
						<DataGridColumn sticky styles={[styleSheet.columnFour]}>
							Actions
						</DataGridColumn>
					</DataGridColumns>

					<DataGridRows>
						{!isLoading &&
							filteredFieldMappings.map((item, index) => {
								const isUnsaved = isFieldMappingUnsaved(item);
								const isDeleted = isFieldMappingDeleted(item);
								const isNew = isFieldMappingNew(item);
								const rowStyles = [];

								if (isDeleted) {
									rowStyles.push(styleSheet.deletedFieldMapping);
								} else if (isNew) {
									rowStyles.push(styleSheet.newFieldMapping);
								} else if (isUnsaved) {
									rowStyles.push(styleSheet.unsavedFieldMapping);
								}

								return (
									<DataGridRow
										key={`row-${item.externalObjectType}-${item.externalFieldName}`}
										styles={rowStyles.length > 0 ? rowStyles : undefined}
									>
										<DataGridRowItem>
											<div className={css(styleSheet.fieldNameContainer)}>
												<div className={css(styleSheet.fieldNameContent)}>
													<h6 className={css(baseStyleSheet.truncateText, baseStyleSheet.m0)}>{item.displayName}</h6>
													<div className={css(baseStyleSheet.truncateText, styleSheet.fieldDescriptionText)}>
														{item.externalFieldName}
													</div>
												</div>
												<div className={css(styleSheet.statusIndicatorContainer)}>
													{isDeleted && <span className={css(styleSheet.deletedIndicator)}>(To be deleted)</span>}
													{!isDeleted && isNew && <span className={css(styleSheet.newIndicator)}>(To be added)</span>}
													{!isDeleted && !isNew && isUnsaved && (
														<span className={css(styleSheet.modifiedIndicator)}>(To be modified)</span>
													)}
												</div>
											</div>
										</DataGridRowItem>
										<DataGridRowItem>
											{item.isRequired ? (
												<Badge text='Required' foregroundColor='#CC0000' />
											) : (
												<Badge text='Optional' foregroundColor='#666666' />
											)}
										</DataGridRowItem>
										<DataGridRowItem>
											{isDeleted ? (
												<div className={css(styleSheet.deletedFieldText)}>
													{getLevitateFieldDisplayName(item.levitateFieldName)}
												</div>
											) : (
												<EditableSelect
													displayValue={getLevitateFieldDisplayName(item.levitateFieldName)}
													value={getLevitateFieldDisplayName(item.levitateFieldName)}
													onValueChange={displayName =>
														onLevitateFieldNameChanged(index, getLevitateFieldPropertyName(String(displayName)))
													}
													customInlineStyles={{ textAlign: 'left' }}
												>
													<SelectTrigger styles={[bs.wFull]}>
														<SelectValue placeholder='Select Levitate field' />
													</SelectTrigger>
													<SelectContent>
														{availableLevitateFieldsQuery.data?.map(field => (
															<SelectItem key={field.propertyName} value={field.name || ''}>
																{field.name}
															</SelectItem>
														))}
													</SelectContent>
												</EditableSelect>
											)}
										</DataGridRowItem>
										<DataGridRowItem>
											{isDeleted ? (
												<Button
													onClick={() => onUndeleteFieldMapping(item)}
													kind='primary'
													size='small'
													disabled={!isEditing}
												>
													Undo Delete
												</Button>
											) : !isNew && isUnsaved ? (
												<Button
													onClick={() => onUndoFieldMappingChanges(item)}
													kind='primary'
													size='small'
													disabled={!isEditing}
												>
													Undo
												</Button>
											) : (
												<Button
													onClick={() => onDeleteFieldMapping(item)}
													kind='destructive'
													size='small'
													disabled={!isEditing}
												>
													<TrashIcon />
												</Button>
											)}
										</DataGridRowItem>
									</DataGridRow>
								);
							})}

						{isLoading ? (
							<DataGridRow>
								<DataGridRowItem fullColSpan>
									<LoadingSpinner type='large' />
								</DataGridRowItem>
							</DataGridRow>
						) : null}
					</DataGridRows>
				</DataGrid>
			</div>

			<Modal
				isOpen={showAddMappingModal}
				onRequestClose={() => onAddMappingModalClose(false)}
				title='Add Field Mapping'
			>
				<div className={css(styleSheet.modalContainer)}>
					<div className={css(styleSheet.modalFieldGroup)}>
						<label className={css(styleSheet.modalLabel)}>External Field</label>
						<Select
							items={availableExternalFieldOptions}
							value={newMapping.externalFieldName || ''}
							onValueChange={fieldName => {
								const fieldInfo = getExternalFieldInfo(fieldName);
								setNewMapping({
									...newMapping,
									externalFieldName: fieldName,
									displayName: fieldInfo?.displayName || fieldName,
								});
							}}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue placeholder='Select external field' />
							</SelectTrigger>
							<SelectContent>
								{availableExternalFieldOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className={css(styleSheet.modalFieldGroup)}>
						<label className={css(styleSheet.modalLabel)}>Levitate Field</label>
						<Select
							value={getLevitateFieldDisplayName(newMapping.levitateFieldName || '')}
							onValueChange={displayName => {
								setNewMapping({
									...newMapping,
									levitateFieldName: getLevitateFieldPropertyName(displayName),
								});
							}}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue placeholder='Select Levitate field' />
							</SelectTrigger>
							<SelectContent>
								{availableLevitateFieldsQuery.data?.map(field => (
									<SelectItem key={field.propertyName} value={field.name || ''}>
										{field.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className={css(styleSheet.modalButtonGroup)}>
						<Button onClick={() => onAddMappingModalClose(false)} kind='reverse'>
							Cancel
						</Button>
						<Button
							onClick={() => onAddMappingModalClose(true)}
							kind='primary'
							disabled={!newMapping.externalFieldName || !newMapping.levitateFieldName}
						>
							Add Mapping
						</Button>
					</div>
				</div>
			</Modal>
		</SettingGroup>
	);
};
